import { clone } from 'lodash';
import Vue from 'vue';
import Component from 'vue-class-component';
import { Emit, Inject, Prop, Provide, Ref } from 'vue-property-decorator';
import EdClassScheduleCalendarComponent from '../ed-class-schedule-calendar/ed-class-schedule-calendar.component';
import { CreateScheduleBlockDto, ResponseCreateScheduleBlockDto, ScheduleBlock, ScheduleBlockItem, ScheduleResponseDto, UpdateScheduleBlockDto } from '@/tools/models/class-builder.models';
import { ScheduleService } from '@/tools/services/schedule.service';
import { ClassesService } from '@/classes/services/classes.service';
import { HolidayService } from '@/tools/services/holiday.service';
import { WORKING_DAYS } from '@/tools/values/schedule.values';
import { ClassScheduleProvider } from '../class-schedule.provider';
import EdClassScheduleRemoveBlockModalComponent from '../ed-class-schedule-remove-block-modal/ed-class-schedule-remove-block-modal.component.vue';
import EdClassScheduleWorkingDaysModal from '../ed-class-schedule-working-days-modal/ed-class-schedule-working-days-modal.component.vue';
import EdCreateBlocksModal from '../ed-create-blocks-modal/ed-create-blocks-modal.component.vue';
import EdClassScheduleShortDaysModal from '../ed-class-schedule-short-days-modal/ed-class-schedule-short-days-modal.component.vue';
import { ScheduleTypeEnum } from '@/tools/enums/schedule-type.enum';
import { EdGenericCalendarWeekdays } from '@/core/components/ed-generic-calendar/interfaces/ed-generic-calendar-weekdays.interface';
import { ScheduleClassResponse } from '@/tools/interfaces/schedule-class-response.interface';
import Moment from 'moment';

@Component({
  name: 'ed-class-schedule',
  components: {
    EdClassScheduleCalendar: () => import(/* "EdClassScheduleCalendar" */ '../ed-class-schedule-calendar/ed-class-schedule-calendar.component.vue'),
    EdClassScheduleHeader: () => import(/* "EdClassScheduleHeader" */ '../ed-class-schedule-header/ed-class-schedule-header.component.vue'),
    EdClassScheduleSidebar: () => import(/* "EdClassScheduleSidebar" */ '../ed-class-schedule-sidebar/ed-class-schedule-sidebar.component.vue'),
    EdClassScheduleHours: () => import(/* "EdClassScheduleHours" */ '../ed-class-schedule-hours/ed-class-schedule-hours.component.vue'),
  },
})
export default class EdClassScheduleComponent extends Vue {
  @Inject('EduacAppSetLoading')
  EduacAppSetLoading: (isLoading: boolean) => void;

  @Provide()
  ScheduleProvider = new ClassScheduleProvider();

  isAddingBlocks = false;
  isEditingBlocks = false;
  blocksCreated: ScheduleBlockItem[] = [];
  blocksCalendarCreated: ScheduleBlock[] = [];
  blockToRemove: ScheduleBlockItem | null = null;
  classId: string = '';
  schedule: any;
  className: string = '';
  isToggleSidebar = true;
  classIds: string[] | null = [];
  allowTeacherToModify = false;
  anyClassNotAllowed = false;
  loading: boolean = true;
  workingDays: any[] = [];
  isCalendarExpired : boolean = false;

  @Ref('scheduleCalendar')  
  scheduleCalendar: EdClassScheduleCalendarComponent;

  get isWeeklySchedule(): boolean {
    return this.ScheduleProvider.isWeeklySchedule;
  }
  
  get weekdays(): number[] | EdGenericCalendarWeekdays[] {
    return this.ScheduleProvider.weekdays;
  }

  get scheduleShortDay(): string {
    const shortDay = this.ScheduleProvider.getScheduleShortDay();
    if (shortDay)
      return this.$t(`common.weekdays.${shortDay.toLowerCase()}`).toString();
    return this.$t('common.select') as string;
  }

  get isAbleToModify(): boolean {
    if(this.isCalendarExpired) 
      return false;
    const alwaysAble = ['SA', 'SDA'].includes(this.$store.state.AuthStore.user?.role);
    if(alwaysAble) return true;
    if(this.anyClassNotAllowed) return false;
    return (this.allowTeacherToModify && this.$store.state.AuthStore.user?.role === 'TEACHER');
  }

  get isZoomInDisabled(): boolean {
    return this.ScheduleProvider.calendarZoomInDisabled;
  }

  get isZoomOutDisabled(): boolean {
    return this.ScheduleProvider.calendarZoomOutDisabled;
  }

  @Emit('onBack')
  emitOnBack() {
    return;
  }

  async created() {
    this.loading = true;
    this.classId = this.$route.params.classId;
    this.classIds = this.$route.query.classIds ? (this.$route.query.classIds as string[]) : null;

    const classPromise = ClassesService.getClass(this.classId);
    const workdaysPromise = HolidayService.getWorkingDaysByClassId(this.classId)
    const schedulePromise = ScheduleService.getScheduleByClassId(Number(this.classId));
    const [classData, workdays, schedule] = await Promise.all([
      classPromise,
      workdaysPromise,
      schedulePromise
    ]);
    this.workingDays = clone(workdays);
    this.className = classData.name;
    this.allowTeacherToModify = classData.allowTeachersModifySchedule;
    this.setWeekdays(classData, workdays);
    this.ScheduleProvider.scheduleStartHour = classData.startScheduleTime;
    this.ScheduleProvider.scheduleEndHour = classData.endScheduleTime;
    this.ScheduleProvider.classId = Number(this.classId);
    this.initScheduleBlocks(schedule);
    this.isCalendarExpired = Moment(classData.schoolDay?.endDate).isBefore(Moment());
    this.loading = false;
  }

  setWeekdays(classData: ScheduleClassResponse, workdays: WORKING_DAYS[]) {
    let scheduleType = classData.scheduleType;

    if (!scheduleType) {
      scheduleType = this.$route.query.scheduleType as ScheduleTypeEnum;
    }

    if (scheduleType === ScheduleTypeEnum.AB) {
      if (classData.scheduleShortDay) {
        this.ScheduleProvider.setScheduleShortDay(classData.scheduleShortDay);
      } else {
        this.ScheduleProvider.setABWeekdays();
      }
    } else {
      this.ScheduleProvider.setWeekdays(workdays);
    }
  }

  initScheduleBlocks(schedule: ScheduleResponseDto[]) {
    this.ScheduleProvider.scheduleBlocksCreated = ScheduleResponseDto.getScheduleBlocks(schedule);
  }

  onActivateSelectMode({ canSelect }) {
    this.ScheduleProvider.scheduleBlockInEdition = null;

    setTimeout(() => {
      this.$EdModal.createModal(EdCreateBlocksModal, {
        scheduleBlockToEdit: this.ScheduleProvider.scheduleBlockInEdition,
        blocksCreated: this.blocksCalendarCreated,
        workingDays: this.weekdays,
        isAbleToModify: this.isAbleToModify,
        onCreateBlocks: this.onCreateBlocks,
        scheduleBlocksCreated: this.ScheduleProvider.scheduleBlocksCreated,
        scheduleBlockInEdition: this.ScheduleProvider.scheduleBlockInEdition,
        weekdays: this.ScheduleProvider.weekdays,
        scheduleStartHour: this.ScheduleProvider.scheduleStartHour,
        scheduleEndHour: this.ScheduleProvider.scheduleEndHour,
        isWeeklySchedule: this.isWeeklySchedule,
        scheduleShortDay: this.ScheduleProvider.getScheduleShortDay()
      })
    }, 100);
  }

  getBlocksItemsFromBlocks(blocks: ScheduleBlock[]): ScheduleBlockItem[] {
    return blocks.map((block) => {
      return new ScheduleBlockItem({
        start: block.start,
        end: block.end,
        day: block.day,
        title: block.title || '',
        description: block.description || '',
        id: block.id || '',
      })
    });
  }

  async onCreateBlocks(block: ScheduleBlock) {
    if (block?.id) {
      await this.updateBlocks(block);
    } else {
      await this.createBlocks(block);
    }
  }

  async createBlocks(block: ScheduleBlock) {
    try {
      this.EduacAppSetLoading(true);
      const request = new CreateScheduleBlockDto(block);
      const response = await ScheduleService.addScheduleEntry(Number(this.classId), request);
      const blockCreated = ResponseCreateScheduleBlockDto.getScheduleBlock(response[0]);
      
      this.ScheduleProvider.scheduleBlocksCreated = [
        ...this.ScheduleProvider.scheduleBlocksCreated,
        blockCreated
      ];
    } catch(error) {
      this.$EdModal.createMessageModal({
        body: error
      });
    } finally {
      this.EduacAppSetLoading(false);
    }
  }

  async updateBlocks(block: ScheduleBlock) {
    try {
      this.EduacAppSetLoading(true);
  
      const request = new UpdateScheduleBlockDto(block);
      const response = await ScheduleService.updateScheduleBlock(block.id, Number(this.classId), request);
      const blockUpdated = ResponseCreateScheduleBlockDto.getScheduleBlock(response[0]);

      this.ScheduleProvider.scheduleBlockInEdition = null;
      this.ScheduleProvider.scheduleBlocksCreated  = this.ScheduleProvider.scheduleBlocksCreated.map((b) => {
        if (b.id === block.id) return blockUpdated;

        return b;
      });
    } catch(error) {
      this.$EdModal.createMessageModal({
        body: error[0]?.message
      });
    } finally {
      this.EduacAppSetLoading(false);
    }
  }

  onEditBlockSelected(block: ScheduleBlock) {
    this.isEditingBlocks = true;
    this.ScheduleProvider.scheduleBlockInEdition = block;

    setTimeout(() => {
      this.$EdModal.createModal(EdCreateBlocksModal, {
        scheduleBlockToEdit: this.ScheduleProvider.scheduleBlockInEdition,
        blocksCreated: this.blocksCalendarCreated,
        workingDays: this.weekdays,
        isAbleToModify: this.isAbleToModify,
        onCreateBlocks: this.onCreateBlocks,
        scheduleBlocksCreated: this.ScheduleProvider.scheduleBlocksCreated,
        scheduleBlockInEdition: this.ScheduleProvider.scheduleBlockInEdition,
        weekdays: this.ScheduleProvider.weekdays,
        scheduleStartHour: this.ScheduleProvider.scheduleStartHour,
        scheduleEndHour: this.ScheduleProvider.scheduleEndHour,
        isWeeklySchedule: this.isWeeklySchedule,
        scheduleShortDay: this.ScheduleProvider.getScheduleShortDay()
      })
    }, 100);
  }

  onDeleteBlockSelected(block: ScheduleBlockItem) {
    this.blockToRemove = block;

    this.$EdModal.createModal(EdClassScheduleRemoveBlockModalComponent, {
      block: this.blockToRemove,
      onRemoveBlock: this.onRemoveBlock
    })
  }

  async onRemoveBlock(block: ScheduleBlock) {
    try {
      this.EduacAppSetLoading(true);
      await ScheduleService.removeScheduleBlock(block.id, Number(this.classId));
      this.ScheduleProvider.scheduleBlocksCreated = this.ScheduleProvider.scheduleBlocksCreated.filter(
        (b) => b.id !== block.id
      );
    } catch(error) {
      this.$EdModal.createMessageModal({
        body: error
      });
    } finally {
      this.EduacAppSetLoading(false);
    }
  }

  onOpenWorkingDaysModal() {
    this.$EdModal.createModal(EdClassScheduleWorkingDaysModal, {
      enabledDays: this.workingDays,
      workingDays: this.weekdays,
      onSaveWorkingDaysSelected: this.onSaveWorkingDaysSelected
    })
  }

  async onSaveWorkingDaysSelected(workingDays: number[]) {
    try {
      await ScheduleService.updateWorkingDays(Number(this.classId), workingDays);
      this.ScheduleProvider.weekdays = workingDays;
    } catch (error) {
      this.$EdModal.createMessageModal({ 
        body: error[0].message
      });
    }
  }

  onToggleSidebar() {
    this.isToggleSidebar = !this.isToggleSidebar;
  }

  onScheduleHoursChanged() {
    this.scheduleCalendar.setIntervalHeight();
  }

  onPlusEmit() {
    this.scheduleCalendar.zoomIn();
  }

  onMinusEmit() {
    this.scheduleCalendar.zoomOut();
  }

  onOpenShortDaysModal() {
    this.$EdModal.createModal(EdClassScheduleShortDaysModal, {}, {
      onAddShortDay: this.onAddShortDay.bind(this),
      scheduleShortDay: this.ScheduleProvider.getScheduleShortDay(),
      workingDays: this.workingDays,
    })
  }

  async onAddShortDay(shortDay: string) {
    if (shortDay) {
      await ScheduleService.updateScheduleTypeOptions(Number(this.classId), ScheduleTypeEnum.AB, shortDay);
      this.ScheduleProvider.setScheduleShortDay(shortDay);
    }
  }
}
